import type { Dispatch, SetStateAction } from "react";
import {
  MFA_DEFAULT_LOCKOUT_DURATION,
  MFA_MAX_FAILED_ATTEMPTS,
  MFA_MAX_SMS_SENDS,
  MFA_RATE_LIMIT_WINDOW,
  mfaLockoutMessage,
} from "./utils";
import type { MfaRateLimitState } from "./types";

export function checkMfaSmsRateLimit(
  rateLimit: MfaRateLimitState,
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>
): string | null {
  const windowStart = rateLimit.smsSendWindowStart ?? 0;
  const sendCount = rateLimit.smsSendCount ?? 0;
  const now = Date.now();
  if (now - windowStart > MFA_RATE_LIMIT_WINDOW) {
    setRateLimit((r) => ({
      ...r,
      smsSendCount: 0,
      smsSendWindowStart: now,
    }));
    return null;
  }
  if (sendCount >= MFA_MAX_SMS_SENDS) {
    const remainingMs =
      MFA_RATE_LIMIT_WINDOW - (now - windowStart);
    const remainingMin = Math.ceil(remainingMs / 60000);
    return `Too many SMS requests. Please try again in ${remainingMin} minute${remainingMin > 1 ? "s" : ""}.`;
  }
  return null;
}

export function recordMfaSmsSend(
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>
): void {
  const now = Date.now();
  setRateLimit((r) => {
    const windowStart = r.smsSendWindowStart ?? 0;
    const sendCount = r.smsSendCount ?? 0;
    const inWindow = now - windowStart < MFA_RATE_LIMIT_WINDOW;
    return {
      ...r,
      smsSendCount: inWindow ? sendCount + 1 : 1,
      smsSendWindowStart: inWindow ? windowStart : now,
    };
  });
}


export function recordMfaFailedAttempt(
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>,
  lockoutMs?: number
): boolean {
  let triggered = false;
  setRateLimit((r) => {
    const newFailed = r.failedAttempts + 1;
    if (newFailed >= MFA_MAX_FAILED_ATTEMPTS) {
      triggered = true;
      return {
        ...r,
        failedAttempts: newFailed,
        lockedUntil: Date.now() + (lockoutMs ?? MFA_DEFAULT_LOCKOUT_DURATION),
      };
    }
    return { ...r, failedAttempts: newFailed };
  });
  return triggered;
}

export function clearMfaFailedAttempts(
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>
): void {
  setRateLimit((r) => ({ ...r, failedAttempts: 0, lockedUntil: null }));
}

export function computeMfaLockedOut(
  lockedUntil: number | null,
  setRateLimit: Dispatch<SetStateAction<MfaRateLimitState>>
): boolean {
  if (!lockedUntil) return false;
  if (Date.now() > lockedUntil) {
    setRateLimit((r) => ({
      ...r,
      failedAttempts: 0,
      lockedUntil: null,
    }));
    return false;
  }
  return true;
}

export function getMfaLockoutMessage(lockedUntil: number | null): string {
  return mfaLockoutMessage(lockedUntil);
}
